import React, { useCallback, useMemo, useState } from 'react';
import { AccountCircle, SearchRounded } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { baseFirmUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { NotificationBell } from 'components/NotificationCenter';
import {
  PrimaryNavigationIconButton,
  PrimaryNavigation as PrimaryNavigationPrimitive,
  PrimaryNavigationSearchButton,
  PrimaryNavigationSkeleton,
  Tooltip,
} from 'components/ui';
import { getCssPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import getRunningBrowserOS, { BrowserOS } from 'helpers/getRunningBrowserOS';
import { stripFirmDocumentsWorkspaceSearchParams } from 'pages/Documents/FirmDocuments/utilities';
import { stripWaterfallCalculationSearchParams } from 'pages/Waterfall/utilities/waterfallSearchParams';
import { getInitials } from 'utilities/getInitials';
import { GUIDED_TOUR_TARGETS } from '../tours';
import AppSwitcher from './AppSwitcher';
import CompanyChip from './CompanyChip';
import MeasurementDateChip from './MeasurementDateChip';
import {
  ENABLED_PRIMARY_NAV_SLUGS,
  PRIMARY_NAV_ITEMS,
  PRIMARY_NAV_SLUGS,
  PRIMARY_NAVIGATION_ARIA_LABEL,
  PRIMARY_NAVIGATION_USER_MENU_ARIA_LABEL,
} from './PrimaryNavigation.constants';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from './PrimaryNavigation.testIds';
import PrimaryNavigationOptionsMenu from './PrimaryNavigationOptionsMenu';

interface PrimaryNavigationProps {
  'data-tour'?: string;
  isCompanyListLoading?: boolean;
  isGlobalCompGroupsRoute?: boolean;
  isNavigationLoading?: boolean;
  onSuperSearchOpen?: () => void;
}

const PrimaryNavigation = ({
  'data-tour': dataTour,
  isCompanyListLoading = false,
  isGlobalCompGroupsRoute = false,
  isNavigationLoading = false,
  onSuperSearchOpen,
}: PrimaryNavigationProps) => {
  const { firmSlugParam } = useParams<{ firmSlugParam: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { zoomLevel } = useZoom();
  const [{ firmInfo, user }] = useStore() as UseStoreValues;
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
  const browserOS = getRunningBrowserOS();
  const superSearchLabel = browserOS === BrowserOS.MacOS
    ? 'Search (⌘K on Mac)'
    : 'Search (Ctrl+K on Windows)';
  const firmIconUrl = isGlobalCompGroupsRoute ? undefined : firmInfo?.icon_url;
  const firmName = isGlobalCompGroupsRoute ? 'Scalar' : firmInfo?.name ?? 'Scalar';
  const activePrimaryNavValue = useMemo(() => {
    if (!firmSlugParam) {
      return false;
    }

    const firmBasePath = baseFirmUrl(firmSlugParam);
    const primarySegment = location.pathname
      .slice(firmBasePath.length)
      .split('/')
      .filter(Boolean)[0];

    return PRIMARY_NAV_ITEMS.some(item => item.slug === primarySegment) ? primarySegment : false;
  }, [firmSlugParam, location.pathname]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, slug: string) => {
      if (!firmSlugParam) {
        return;
      }

      const search = (() => {
        if (activePrimaryNavValue === PRIMARY_NAV_SLUGS.WATERFALLS) {
          return stripWaterfallCalculationSearchParams(location.search);
        }

        if (activePrimaryNavValue === PRIMARY_NAV_SLUGS.DOCUMENTS && slug !== PRIMARY_NAV_SLUGS.DOCUMENTS) {
          return stripFirmDocumentsWorkspaceSearchParams(location.search);
        }

        return location.search;
      })();

      navigate(`${baseFirmUrl(firmSlugParam)}/${slug}${search}`);
    },
    [firmSlugParam, navigate, location.search, activePrimaryNavValue]
  );

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  if (isNavigationLoading) {
    return (
      <PrimaryNavigationSkeleton
        actionCount={user?.is_staff ? 3 : 2}
        contextChipCount={2}
        itemCount={PRIMARY_NAV_ITEMS.length}
        showSearchAction={Boolean(onSuperSearchOpen)}
        zoomLevel={zoomLevel}
      />
    );
  }
  const userIconSize = getCssPx(32);
  const firmBrandSlot = (
    <Tooltip placement="bottom" title={firmName}>
      <span
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          height: '100%',
          justifyContent: 'center',
          width: '100%',
        }}>
        {firmIconUrl ? (
          <img
            src={firmIconUrl}
            alt={`${firmName} firm icon`}
            style={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
          />
        ) : (
          <Avatar
            aria-label={`${firmName} firm icon`}
            variant="rounded"
            sx={theme => ({
              backgroundColor: theme.palette.common.white,
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: theme.spacing(1.5),
              color: theme.palette.primary.main,
              fontSize: getCssPx(14),
              fontWeight: 'bold',
              height: getCssPx(32),
              width: getCssPx(32),
            })}>
            {getInitials({ name: firmName })}
          </Avatar>
        )}
      </span>
    </Tooltip>
  );
  const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || user?.email || 'User';
  const userIcon = user?.profile?.picture_url ? (
    <Avatar
      src={user.profile.picture_url}
      data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.userAvatar}
      sx={theme => ({
        backgroundColor: theme.palette.neutral[300],
        border: `${getCssPx(2)} solid ${theme.palette.brand[500]}`,
        boxSizing: 'border-box',
        height: userIconSize,
        width: userIconSize,
      })}
    />
  ) : (
    <AccountCircle
      data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.userAvatarFallback}
      sx={{ width: userIconSize, height: userIconSize }}
    />
  );

  return (
    <>
      <PrimaryNavigationPrimitive
        ariaLabel={PRIMARY_NAVIGATION_ARIA_LABEL}
        brandSlot={firmBrandSlot}
        data-tour={dataTour}
        contextSlot={!isGlobalCompGroupsRoute ? (
          <>
            <CompanyChip isLoading={isCompanyListLoading} zoomLevel={zoomLevel}/>
            <MeasurementDateChip zoomLevel={zoomLevel}/>
          </>
        ) : undefined}
        actionsSlot={(
          <>
            {onSuperSearchOpen && (
              <PrimaryNavigationSearchButton
                aria-label="Open Super Search"
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.superSearchButton}
                onClick={onSuperSearchOpen}
                zoomLevel={zoomLevel}>
                <SearchRounded />
                {superSearchLabel}
              </PrimaryNavigationSearchButton>
            )}
            <NotificationBell />
            {user?.is_staff && <AppSwitcher zoomLevel={zoomLevel}/>}
            <Tooltip placement="bottom" title={userName}>
              <PrimaryNavigationIconButton
                aria-label={PRIMARY_NAVIGATION_USER_MENU_ARIA_LABEL}
                aria-controls={userMenuAnchorEl ? FIRM_PRIMARY_NAVIGATION_TEST_IDS.optionsMenu : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(userMenuAnchorEl)}
                isInteractive
                onClick={handleUserMenuOpen}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.userMenuButton}
                data-tour={GUIDED_TOUR_TARGETS.utilitiesMenu}
                zoomLevel={zoomLevel}>
                {userIcon}
              </PrimaryNavigationIconButton>
            </Tooltip>
          </>
        )}
        items={(isGlobalCompGroupsRoute ? [] : PRIMARY_NAV_ITEMS).map(item => ({
          label: item.label,
          value: item.slug,
          disabled: !ENABLED_PRIMARY_NAV_SLUGS.has(item.slug),
        }))}
        onChange={handleTabChange}
        value={activePrimaryNavValue}
        zoomLevel={zoomLevel}
      />
      <PrimaryNavigationOptionsMenu
        anchorEl={userMenuAnchorEl}
        isGlobalCompGroupsRoute={isGlobalCompGroupsRoute}
        onClose={handleUserMenuClose}
        zoomLevel={zoomLevel}
      />
    </>
  );
};

export default PrimaryNavigation;
